import { PublicClientApplication, SilentRequest } from '@azure/msal-browser';
import { loginRequest } from '../../config/authConfig';

/**
 * Interface para datos mínimos de sesión
 */
interface SessionMetadata {
    accountId: string;
    username: string;
    lastActivity: number;
    sessionStarted: number;
    environment: string;
}

/**
 * Servicio para mantener persistencia de sesión con memoryStorage
 * Almacena solo metadatos mínimos, no tokens
 */
export class SessionPersistenceService {
    private static readonly SESSION_KEY = 'msal_session_metadata';
    private static readonly MAX_SESSION_DURATION = 8 * 60 * 60 * 1000; // 8 horas
    private static readonly ACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutos de inactividad

    /**
     * Guarda metadatos de sesión (NO tokens)
     */
    static saveSessionMetadata(account: any): void {
        try {
            const metadata: SessionMetadata = {
                accountId: account.homeAccountId,
                username: account.username,
                lastActivity: Date.now(),
                sessionStarted: Date.now(),
                environment: account.environment
            };

            sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(metadata));
            console.log('Session metadata saved');
        } catch (error) {
            console.warn('Failed to save session metadata:', error);
        }
    }

    /**
     * Obtiene metadatos de sesión guardados
     */
    static getSessionMetadata(): SessionMetadata | null {
        try {
            const stored = sessionStorage.getItem(this.SESSION_KEY);
            if (!stored) return null;

            const metadata: SessionMetadata = JSON.parse(stored);
            
            // Verificar expiración por tiempo
            if (Date.now() - metadata.sessionStarted > this.MAX_SESSION_DURATION) {
                this.clearSessionMetadata();
                console.log('Session expired by time');
                return null;
            }

            // Verificar expiración por inactividad
            if (Date.now() - metadata.lastActivity > this.ACTIVITY_TIMEOUT) {
                this.clearSessionMetadata();
                console.log('Session expired by inactivity');
                return null;
            }

            return metadata;
        } catch (error) {
            console.warn('Failed to get session metadata:', error);
            return null;
        }
    }

    /**
     * Actualiza la última actividad
     */
    static updateActivity(): void {
        try {
            const metadata = this.getSessionMetadata();
            if (metadata) {
                metadata.lastActivity = Date.now();
                sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(metadata));
            }
        } catch (error) {
            console.warn('Failed to update activity:', error);
        }
    }

    /**
     * Limpia metadatos de sesión
     */
    static clearSessionMetadata(): void {
        try {
            sessionStorage.removeItem(this.SESSION_KEY);
            console.log('Session metadata cleared');
        } catch (error) {
            console.warn('Failed to clear session metadata:', error);
        }
    }

    /**
     * Verifica si hay una sesión válida guardada
     */
    static hasValidSessionMetadata(): boolean {
        return this.getSessionMetadata() !== null;
    }

    /**
     * Intenta restaurar la sesión usando solo metadatos
     */
    static async attemptSessionRestore(msalInstance: PublicClientApplication): Promise<boolean> {
        try {
            const metadata = this.getSessionMetadata();
            if (!metadata) {
                console.log('No session metadata found');
                return false;
            }

            console.log('Attempting to restore session for:', metadata.username);

            // Verificar si MSAL tiene cuentas que coincidan
            const accounts = msalInstance.getAllAccounts();
            const matchingAccount = accounts.find(acc => 
                acc.homeAccountId === metadata.accountId && 
                acc.username === metadata.username
            );

            if (!matchingAccount) {
                console.log('No matching MSAL account found');
                this.clearSessionMetadata();
                return false;
            }

            // Establecer cuenta activa
            msalInstance.setActiveAccount(matchingAccount);

            // Intentar obtener token de forma silenciosa para verificar validez
            const silentRequest: SilentRequest = {
                scopes: loginRequest.scopes || ['User.Read'],
                account: matchingAccount,
                forceRefresh: false
            };

            const response = await msalInstance.acquireTokenSilent(silentRequest);
            
            if (response.accessToken) {
                console.log('Session restored successfully');
                this.updateActivity(); // Actualizar actividad
                return true;
            } else {
                console.log('Failed to get token, session invalid');
                this.clearSessionMetadata();
                return false;
            }

        } catch (error) {
            console.warn('Session restore failed:', error);
            this.clearSessionMetadata();
            return false;
        }
    }

    /**
     * Configura tracking de actividad del usuario
     */
    static setupActivityTracking(): () => void {
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        
        const updateActivity = () => {
            this.updateActivity();
        };

        // Agregar listeners
        events.forEach(event => {
            document.addEventListener(event, updateActivity, true);
        });

        // Retornar función de cleanup
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, updateActivity, true);
            });
        };
    }

    /**
     * Obtiene información de debug sobre la sesión
     */
    static getDebugInfo(): {
        hasMetadata: boolean;
        metadata: SessionMetadata | null;
        isExpired: boolean;
        timeUntilExpiry: number;
        timeUntilInactivityExpiry: number;
    } {
        const metadata = this.getSessionMetadata();
        const now = Date.now();
        
        return {
            hasMetadata: !!metadata,
            metadata,
            isExpired: metadata ? (now - metadata.sessionStarted > this.MAX_SESSION_DURATION) : true,
            timeUntilExpiry: metadata ? Math.max(0, this.MAX_SESSION_DURATION - (now - metadata.sessionStarted)) : 0,
            timeUntilInactivityExpiry: metadata ? Math.max(0, this.ACTIVITY_TIMEOUT - (now - metadata.lastActivity)) : 0
        };
    }
}